import React, { useState, useEffect } from "react";
import { Input, Card, Button, Row, Col, message, Tabs, Select, Space, Modal, Timeline, Typography, Spin } from "antd";
import { EditOutlined, SaveOutlined, HistoryOutlined, RollbackOutlined, ExclamationCircleOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

const { TextArea } = Input;
const { TabPane } = Tabs;

interface CategoryPrompt {
  id: string;
  category: string;
  examples: string[];
  prompt: string;
}

interface PromptsResponse {
  category_prompts: Record<string, CategoryPrompt>;
  final_evaluation_prompt: string;
}

interface PromptHistoryItem {
  id: string;
  category_id: string;
  category_name: string;
  examples: string[];
  prompt: string;
  modified_by: string;
  modified_at: string;
}

interface GeneralPromptHistoryItem {
  id: string;
  final_evaluation_prompt: string;
  modified_by: string;
  modified_at: string;
}

// Utility to generate a unique id for each example
const generateExampleId = () => Math.random().toString(36).substr(2, 9);

// Helper to convert string[] to [{id, value}] for UI
function toExampleObjects(examples: string[]): { id: string; value: string }[] {
  return examples.map((value) => ({ id: generateExampleId(), value }));
}

// Helper to convert [{id, value}] to string[]
function fromExampleObjects(examples: { id: string; value: string }[]): string[] {
  return examples.map(e => e.value);
}

interface CategoryPromptHistoryProps {
  categoryId: string;
  onRevert: (historyItem: PromptHistoryItem) => Promise<void>;
  onClose: () => void;
}

interface GeneralPromptHistoryProps {
  onRevert: (historyItem: GeneralPromptHistoryItem) => Promise<void>;
  onClose: () => void;
}

const CategoryPromptHistory: React.FC<CategoryPromptHistoryProps> = ({ categoryId, onRevert, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [history, setHistory] = useState<PromptHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/category-prompts/history/${categoryId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('History fetch error:', errorText);
        throw new Error(`Failed to fetch prompt history: ${errorText}`);
      }
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error('Error in fetchHistory:', error);
      message.error("Error fetching prompt history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [categoryId]);

  return (
    <Modal
      title={`Prompt History`}
      open={true}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Spin spinning={loading}>
        <Timeline
          items={history.map((item) => ({
            color: 'blue',
            key: item.id,
            children: (
              <div style={{ marginBottom: 20 }} key={item.id}>
                <Typography.Title level={5}>
                  {new Date(item.modified_at).toLocaleString()}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Modified by: {item.modified_by}
                </Typography.Text>
                {item.examples?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <Typography.Title level={5}>Examples:</Typography.Title>
                    <ul>
                      {item.examples.slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((example, index) => (
                        <li key={index}>{example}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ marginTop: 10, position: 'relative' }}>
                  <Typography.Title level={5}>Prompt:</Typography.Title>
                  <Typography.Paragraph
                    style={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      overflow: 'visible'
                    }}
                  >
                    {item.prompt}
                  </Typography.Paragraph>
                </div>
                <Button
                  type="primary"
                  icon={<RollbackOutlined />}
                  onClick={() => onRevert(item)}
                  style={{ marginTop: 10 }}
                >
                  Revert to this version
                </Button>
              </div>
            ),
          }))}
        />
      </Spin>
    </Modal>
  );
};

const GeneralPromptHistory: React.FC<GeneralPromptHistoryProps> = ({ onRevert, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [history, setHistory] = useState<GeneralPromptHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/final-evaluation-prompt/history`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('History fetch error:', errorText);
        throw new Error(`Failed to fetch general prompt history: ${errorText}`);
      }
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error('Error in fetchHistory:', error);
      message.error("Error fetching general prompt history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <Modal
      title="Prompt History - Final Evaluation"
      open={true}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Spin spinning={loading}>
        <Timeline
          items={history.map((item) => ({
            color: 'blue',
            key: item.id,
            children: (
              <div style={{ marginBottom: 20 }} key={item.id}>
                <Typography.Title level={5}>
                  {new Date(item.modified_at).toLocaleString()}
                </Typography.Title>
                <Typography.Text type="secondary">
                  Modified by: {item.modified_by}
                </Typography.Text>
                <div style={{ marginTop: 10, position: 'relative' }}>
                  <Typography.Title level={5}>Prompt:</Typography.Title>
                  <Typography.Paragraph
                    style={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      overflow: 'visible'
                    }}
                  >
                    {item.final_evaluation_prompt}
                  </Typography.Paragraph>
                </div>
                <Button
                  type="primary"
                  icon={<RollbackOutlined />}
                  onClick={() => onRevert(item)}
                  style={{ marginTop: 10 }}
                >
                  Revert to this version
                </Button>
              </div>
            ),
          }))}
        />
      </Spin>
    </Modal>
  );
};

export const PromptEditor: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [prompts, setPrompts] = useState<PromptsResponse | null>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showCategoryHistory, setShowCategoryHistory] = useState(false);
  const [showGeneralHistory, setShowGeneralHistory] = useState(false);
  const [localExamples, setLocalExamples] = useState<{ id: string; value: string }[]>([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newExamples, setNewExamples] = useState<{ id: string; value: string }[]>([]);
  const [newPrompt, setNewPrompt] = useState("");
  const [editingCategory, setEditingCategory] = useState(false);
  const [editedCategoryName, setEditedCategoryName] = useState("");

  // Update local examples when category changes
  useEffect(() => {
    if (selectedCategoryId && prompts?.category_prompts[selectedCategoryId]) {
      setLocalExamples(toExampleObjects(prompts.category_prompts[selectedCategoryId].examples));
    }
    // Only run this effect when the selected category changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId]);

  // Handle example changes
  const handleExampleChange = (index: number, value: string) => {
    setLocalExamples(localExamples.map((ex, i) =>
      i === index ? { ...ex, value } : ex
    ));
  };

  // Handle example removal
  const handleExampleRemove = (index: number) => {
    setLocalExamples(localExamples.filter((_, i) => i !== index));
  };

  // Handle adding new example
  const handleAddExample = () => {
    setLocalExamples([
      ...localExamples,
      { id: generateExampleId(), value: "" }
    ]);
  };

  // Fetch all prompts from the backend
  const fetchPrompts = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to fetch prompts");

      const data = await response.json();
      setPrompts(data);
      if (Object.keys(data.category_prompts).length > 0) {
        const sortedCategories = Object.keys(data.category_prompts).sort((a, b) => 
          data.category_prompts[a].category.localeCompare(data.category_prompts[b].category)
        );
        setSelectedCategoryId(sortedCategories[0]);
      }
    } catch (error) {
      message.error("Error fetching prompts.");
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const handleRevertCategoryPrompt = async (historyItem: PromptHistoryItem) => {
    if (!selectedCategoryId) return;
    
    setLoading(prev => ({ ...prev, [selectedCategoryId]: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts/medical/${selectedCategoryId}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category_id: selectedCategoryId,
          category: prompts?.category_prompts[selectedCategoryId]?.category || "",
          prompt: historyItem.prompt,
          examples: historyItem.examples,
        }),
      });
      if (!response.ok) throw new Error("Failed to revert prompt");
      const updatedData = await response.json();
      setPrompts(updatedData);
      setLocalExamples(toExampleObjects(updatedData.category_prompts[selectedCategoryId].examples));
      message.success("Successfully reverted to previous version!");
      setShowCategoryHistory(false);
    } catch (error) {
      message.error("Error reverting prompt. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, [selectedCategoryId]: false }));
    }
  };

  const handleRevertGeneralPrompt = async (historyItem: GeneralPromptHistoryItem) => {
    setLoading(prev => ({ ...prev, final_evaluation: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts/final-evaluation`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt: historyItem.final_evaluation_prompt }),
      });
      if (!response.ok) throw new Error("Failed to revert prompt");
      const updatedData = await response.json();
      setPrompts(updatedData);
      message.success("Successfully reverted to previous version!");
      setShowGeneralHistory(false);
    } catch (error) {
      message.error("Error reverting prompt. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, final_evaluation: false }));
    }
  };

  // Save category-specific prompt
  const handleSaveCategoryPrompt = async (categoryId: string, prompt: string) => {
    setLoading(prev => ({ ...prev, [categoryId]: true }));
    try {
      const token = await getAccessTokenSilently();
      const examples = fromExampleObjects(localExamples);
      const body = {
        category_id: categoryId,
        category: prompts?.category_prompts[categoryId]?.category || "",
        prompt,
        examples,
      };
      const response = await fetch(`${API_BASE_URL}/api/prompts/medical/${categoryId}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Failed to save prompt");

      const updatedData = await response.json();
      setPrompts(updatedData);
      // Update localExamples to match saved data
      setLocalExamples(toExampleObjects(updatedData.category_prompts[categoryId].examples));
      message.success(`Prompt saved successfully!`);
    } catch (error) {
      message.error(`Error saving prompt. Please try again.`);
    } finally {
      setLoading(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  // Save final evaluation prompt
  const handleSaveFinalEvaluationPrompt = async (prompt: string) => {
    setLoading(prev => ({ ...prev, final_evaluation: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts/final-evaluation`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) throw new Error("Failed to save prompt");

      const updatedData = await response.json();
      setPrompts(updatedData);
      message.success("Final evaluation prompt saved successfully!");
    } catch (error) {
      message.error("Error saving final evaluation prompt. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, final_evaluation: false }));
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      message.error("Category name is required.");
      return;
    }
    if (!newPrompt.trim()) {
      message.error("Prompt is required.");
      return;
    }
    setLoading(prev => ({ ...prev, add: true }));
    try {
      const token = await getAccessTokenSilently();
      const body = {
        category: newCategoryName.trim(),
        prompt: newPrompt,
        examples: fromExampleObjects(newExamples),
      };
      const response = await fetch(`${API_BASE_URL}/api/prompts/medical`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to add category");
      }
      const updatedData = await response.json();
      setPrompts(updatedData);
      // Find the newly added category ID
      const newCategoryId = Object.keys(updatedData.category_prompts).find(
        id => updatedData.category_prompts[id].category === newCategoryName.trim()
      );
      if (newCategoryId) {
        setSelectedCategoryId(newCategoryId);
      }
      setAddModalVisible(false);
      setNewCategoryName("");
      setNewExamples([]);
      setNewPrompt("");
      message.success("Category added successfully!");
    } catch (error: any) {
      message.error(error.message || "Error adding category. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, add: false }));
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    setLoading(prev => ({ ...prev, delete: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts/medical/${categoryId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to delete category");
      }
      const updatedData = await response.json();
      setPrompts(updatedData);
      // Select the first category if any remain
      const categories = Object.keys(updatedData.category_prompts);
      setSelectedCategoryId(categories.length > 0 ? categories[0] : null);
      message.success("Category deleted successfully!");
    } catch (error: any) {
      message.error(error.message || "Error deleting category. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const showDeleteConfirm = (categoryId: string) => {
    const categoryName = prompts?.category_prompts[categoryId]?.category || categoryId;
    Modal.confirm({
      title: `Delete category "${categoryName}"?`,
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to delete this category? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        handleDeleteCategory(categoryId);
      },
    });
  };

  const handleStartEditCategory = () => {
    if (!selectedCategoryId || !prompts) return;
    setEditingCategory(true);
    setEditedCategoryName(prompts.category_prompts[selectedCategoryId].category);
  };

  const handleSaveEditCategory = async () => {
    if (!editedCategoryName.trim() || !selectedCategoryId) {
      message.error("Category name cannot be empty.");
      return;
    }
    setLoading(prev => ({ ...prev, rename: true }));
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/prompts/medical/${selectedCategoryId}/rename`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_name: editedCategoryName.trim() }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to rename category");
      }
      const updatedData = await response.json();
      setPrompts(updatedData);
      setEditingCategory(false);
      message.success("Category renamed successfully!");
    } catch (error: any) {
      message.error(error.message || "Error renaming category. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, rename: false }));
    }
  };

  if (!prompts) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ width: "100%", padding: "20px" }}>
      <Tabs defaultActiveKey="category" style={{ marginBottom: "20px" }}>
        <TabPane tab="Category Prompts" key="category">
          <Space direction="vertical" style={{ width: "100%", marginBottom: "20px" }}>
            <Space>
              <Select
                style={{ width: 300 }}
                value={selectedCategoryId}
                onChange={setSelectedCategoryId}
                options={
                  Object.entries(prompts.category_prompts)
                    .sort(([, a], [, b]) => a.category.localeCompare(b.category))
                    .map(([id, category]) => ({
                      label: category.category,
                      value: id
                    }))
                }
              />
              <Button
                icon={<PlusOutlined />}
                onClick={() => setAddModalVisible(true)}
                type="primary"
              >
                Add Category
              </Button>
              {selectedCategoryId && (
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => showDeleteConfirm(selectedCategoryId)}
                  loading={loading.delete}
                >
                  Delete Category
                </Button>
              )}
            </Space>
          </Space>
          
          {selectedCategoryId && prompts.category_prompts[selectedCategoryId] && (
            <Card 
              bordered={false} 
              style={{ boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)" }}
              title={
                <Space>
                  {editingCategory ? (
                    <Input
                      value={editedCategoryName}
                      onChange={e => setEditedCategoryName(e.target.value)}
                      onPressEnter={handleSaveEditCategory}
                      style={{ width: 200, marginRight: 8 }}
                      autoFocus
                      size="small"
                      disabled={loading.rename}
                    />
                  ) : (
                    <span style={{ marginRight: 8 }}>
                      {prompts.category_prompts[selectedCategoryId].category}
                    </span>
                  )}
                  <Button
                    icon={<EditOutlined />}
                    type="text"
                    onClick={handleStartEditCategory}
                    disabled={editingCategory}
                    style={{ padding: 0, marginLeft: 0 }}
                  />
                  {editingCategory && (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleSaveEditCategory}
                        loading={loading.rename}
                        style={{ marginLeft: 4 }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setEditingCategory(false)}
                        style={{ marginLeft: 4 }}
                        disabled={loading.rename}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  <Button 
                    icon={<HistoryOutlined />} 
                    onClick={() => setShowCategoryHistory(true)}
                    type="text"
                  >
                    View History
                  </Button>
                </Space>
              }
            >
              <div style={{ marginBottom: "16px" }}>
                <h4>Examples:</h4>
                <ul style={{ paddingLeft: 0, listStyle: "none" }}>
                  {localExamples.map((exampleObj, index) => (
                    <li key={exampleObj.id} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                      <Input
                        value={exampleObj.value}
                        onChange={e => handleExampleChange(index, e.target.value)}
                        style={{ marginRight: 8 }}
                        size="small"
                      />
                      <Button
                        danger
                        size="small"
                        onClick={() => handleExampleRemove(index)}
                        style={{ marginLeft: 4 }}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
                <Button
                  size="small"
                  onClick={handleAddExample}
                  style={{ marginTop: 4 }}
                >
                  Add Example
                </Button>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <h4>Prompt Template:</h4>
                <Typography.Paragraph type="secondary" style={{ marginBottom: "8px" }}>
                  Use the following variables in your prompt:
                  <ul>
                    <li><code>{'{category}'}</code> - Will be replaced with the name of the selected category</li>
                    <li><code>{'{examples}'}</code> - Will be replaced with a comma-separated list of the examples above</li>
                  </ul>
                </Typography.Paragraph>
                <TextArea
                  value={prompts.category_prompts[selectedCategoryId].prompt}
                  onChange={(e) => {
                    setPrompts(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        category_prompts: {
                          ...prev.category_prompts,
                          [selectedCategoryId]: {
                            ...prev.category_prompts[selectedCategoryId],
                            prompt: e.target.value
                          }
                        }
                      };
                    });
                  }}
                  rows={8}
                />
              </div>
              <Button
                block
                type="primary"
                onClick={() => {
                  handleSaveCategoryPrompt(selectedCategoryId, prompts.category_prompts[selectedCategoryId].prompt);
                }}
                style={{ marginTop: "10px" }}
                loading={loading[selectedCategoryId]}
                icon={<SaveOutlined />}
              >
                Save Prompt
              </Button>
            </Card>
          )}
        </TabPane>
        
        <TabPane tab="Final Evaluation Prompt" key="final">
          <Card 
            bordered={false} 
            style={{ boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)" }}
            title={
              <Space>
                <EditOutlined />
                Final Evaluation Prompt
                <Button 
                  icon={<HistoryOutlined />} 
                  onClick={() => setShowGeneralHistory(true)}
                  type="text"
                >
                  View History
                </Button>
              </Space>
            }
          >
            <Typography.Paragraph type="secondary" style={{ marginBottom: "8px" }}>
              Use the following variables in your prompt:
              <ul>
                <li><code>{'{medical_document_text}'}</code> - Will be replaced with the full medical document text being analyzed</li>
                <li><code>{'{condition_name}'}</code> - Will be replaced with the name of the medical condition</li>
                <li><code>{'{condition_description}'}</code> - Will be replaced with the detailed description of the condition</li>
                <li><code>{'{condition_status}'}</code> - Will be replaced with the current status of the condition</li>
                <li><code>{'{icd10_codes}'}</code> - Will be replaced with a formatted list of relevant ICD-10 codes and descriptions found from vector search</li>
                <li><code>{'{snippets}'}</code> - Will be replaced with all the snippets of text from the medical document that are relevant to the condition</li>
              </ul>
            </Typography.Paragraph>
            <TextArea
              value={prompts.final_evaluation_prompt}
              onChange={(e) => {
                setPrompts(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    final_evaluation_prompt: e.target.value
                  };
                });
              }}
              rows={8}
            />
            <Button
              block
              type="primary"
              onClick={() => {
                handleSaveFinalEvaluationPrompt(prompts.final_evaluation_prompt);
              }}
              style={{ marginTop: "10px" }}
              loading={loading.final_evaluation}
              icon={<SaveOutlined />}
            >
              Save Final Evaluation Prompt
            </Button>
          </Card>
        </TabPane>
      </Tabs>

      {showCategoryHistory && selectedCategoryId && (
        <CategoryPromptHistory
          categoryId={selectedCategoryId}
          onRevert={handleRevertCategoryPrompt}
          onClose={() => setShowCategoryHistory(false)}
        />
      )}

      {showGeneralHistory && (
        <GeneralPromptHistory
          onRevert={handleRevertGeneralPrompt}
          onClose={() => setShowGeneralHistory(false)}
        />
      )}

      {/* Add Category Modal */}
      <Modal
        title="Add New Category"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={handleAddCategory}
        confirmLoading={loading.add}
        okText="Add"
      >
        <Input
          placeholder="Category Name (e.g. Diabetes)"
          value={newCategoryName}
          onChange={e => setNewCategoryName(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div style={{ marginBottom: 12 }}>
          <h4>Examples:</h4>
          <ul style={{ paddingLeft: 0, listStyle: "none" }}>
            {newExamples.map((exampleObj, index) => (
              <li key={exampleObj.id} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <Input
                  value={exampleObj.value}
                  onChange={e => {
                    const newArr = newExamples.map((ex, i) =>
                      i === index ? { ...ex, value: e.target.value } : ex
                    );
                    setNewExamples(newArr);
                  }}
                  style={{ marginRight: 8 }}
                  size="small"
                />
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    setNewExamples(newExamples.filter((_, i) => i !== index));
                  }}
                  style={{ marginLeft: 4 }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
          <Button
            size="small"
            onClick={() => {
              setNewExamples([...newExamples, { id: generateExampleId(), value: "" }]);
            }}
            style={{ marginTop: 4 }}
          >
            Add Example
          </Button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <h4>Prompt Template:</h4>
          <TextArea
            value={newPrompt}
            onChange={e => setNewPrompt(e.target.value)}
            rows={6}
          />
        </div>
      </Modal>
    </div>
  );
};
